import React from 'react';
import PageHero from '../components/PageHero';
import { Helmet } from 'react-helmet-async';
import CTASection from '../components/CTASection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Sleepkids from '../assets/Hero/sleep-kids.svg';
import ChildrenSignsCarousel from '../components/ChildrenSignsCarousel';

const { FiClock, FiStar, FiShield, FiHeart } = FiIcons;

export default function SleepApneaChildren() {
  return (
    <div>

       {/* ✅ Helmet Added */}
      <Helmet>
        <title>Sleep Apnea in Children | Pediatric Airway Treatment</title>
        <meta
          name="description"
          content="Identify and treat sleep apnea in children early. Improve breathing, jaw development, behavior, and long-term health with airway-focused pediatric care."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalCondition",
            "name": "Pediatric Sleep Apnea",
            "description": "A condition where children experience breathing interruptions during sleep due to airway obstruction.",
            "possibleTreatment": [
              {
                "@type": "MedicalTherapy",
                "name": "Myofunctional Therapy"
              },
              {
                "@type": "MedicalProcedure",
                "name": "Airway Orthodontics"
              }
            ]
          })}
        </script>
      </Helmet>

      <PageHero
        title="Sleep Apnea in Children"
        subtitle="Identify airway issues early and guide proper jaw development for better sleep, behavior, and long-term health."
        image={Sleepkids}
      />

      {/* Intro */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
            Pediatric Sleep Apnea
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-6">
            Does Your Child Have Sleep Apnea?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Pediatric sleep apnea often looks very different from adults. Instead of obvious fatigue,
            children may show behavioral issues, poor growth, or developmental concerns — all linked
            to restricted airway and poor oxygen flow during sleep.
          </p>
        </div>
      </section>

      {/* Signs Carousel */}
      <ChildrenSignsCarousel />

      {/* Root Cause Explanation */}
      <section className="px-6 bg-white">
        <div className="max-w-4xl mx-auto bg-accent1/10 p-8 rounded-2xl border border-accent1/20">
          <h3 className="text-2xl font-bold text-secondary mb-4 text-center">
            The Root Cause: Airway &amp; Jaw Development
          </h3>
          <p className="text-gray-600 mb-4">
            When a child's airway is narrow, their body struggles to breathe during sleep. This leads
            to repeated micro-arousals, preventing deep, restorative sleep and forcing the body into
            constant stress mode.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>
              <strong>Behavior &amp; Focus:</strong> Poor sleep often appears as ADHD-like symptoms,
              irritability, and poor academic performance.
            </li>
            <li>
              <strong>Facial Growth:</strong> Mouth breathing and low tongue posture cause the upper
              jaw to develop narrow, leading to crooked teeth and reduced airway space.
            </li>
            <li>
              <strong>Systemic Impact:</strong> Bed wetting, night sweats, and teeth grinding are
              signs of stress caused by poor oxygen levels during sleep.
            </li>
          </ul>
        </div>
      </section>

      {/* Why Early Treatment Matters & Methods */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">

          <div>
            <div className="flex items-center gap-4 mb-6">
              <SafeIcon icon={FiClock} className="text-3xl text-primary" />
              <h2 className="text-3xl font-bold text-secondary">
                Why Early Treatment Matters
              </h2>
            </div>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              In children, the jaw and airway are still developing. This gives us a critical window
              to correct the root cause of sleep apnea — not just manage symptoms.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Early intervention can guide proper jaw growth, improve nasal breathing, and prevent
              the need for invasive procedures like MARPE or DOME in adulthood.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-4 mb-6">
              <SafeIcon icon={FiStar} className="text-3xl text-primary" />
              <h2 className="text-3xl font-bold text-secondary">
                Our Treatment Approach
              </h2>
            </div>

            <ul className="space-y-6">
              <li className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-primary">
                <h4 className="font-bold text-secondary text-xl mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiShield} className="text-primary" />
                  Airway Orthodontics &amp; Palatal Expansion
                </h4>
                <p className="text-gray-600">
                  We use growth-guided expansion techniques to widen the upper jaw naturally. This
                  improves nasal breathing, creates space for teeth, and supports proper airway
                  development.
                </p>
              </li>

              <li className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-primary">
                <h4 className="font-bold text-secondary text-xl mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiHeart} className="text-primary" />
                  Myofunctional Therapy
                </h4>
                <p className="text-gray-600">
                  We train children to develop proper breathing habits, tongue posture, and muscle
                  function — ensuring long-term stability and preventing relapse.
                </p>
              </li>

              <li className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-primary">
                <h4 className="font-bold text-secondary text-xl mb-2">
                  Future Prevention (MARPE &amp; DOME)
                </h4>
                <p className="text-gray-600">
                  By addressing airway issues early, we reduce the likelihood of needing advanced
                  adult treatments like MARPE (non-surgical expansion) or DOME (surgical airway
                  expansion) later in life.
                </p>
              </li>
            </ul>
          </div>

        </div>
      </section>

      {/* Parent Guidance */}
      <section className="py-24 px-6 bg-secondary text-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold mb-8">
            Don't Ignore the Signs
          </h2>
          <p className="text-xl md:text-2xl font-light leading-relaxed mb-12">
            If your child snores, breathes through their mouth, or struggles with focus and energy,
            it may not be behavioral — it may be airway-related.
          </p>
          <p className="text-lg md:text-xl font-light italic text-white/90">
            Early evaluation can change your child's growth, sleep quality, and long-term health.
          </p>
        </div>
      </section>

      <CTASection
        title="Help Your Child Breathe, Sleep, and Grow Better"
        subtitle="Book a pediatric airway evaluation to identify the root cause and start early treatment."
      />
    </div>
  );
}